import { useState, useEffect } from 'react';
import { apiService } from '../services/api.service';

export interface Product {
  id: string;
  sku: string;
  name: string;
  description: string | null;
  price: number;
  costPrice: number | null;
  images: string[];
  categoryId: string;
  isActive: boolean;
  isFeatured: boolean;
  calories: number | null;
  protein: number | null;
  carbs: number | null;
  fat: number | null;
  slug: string | null;
  createdAt: string;
  updatedAt: string;
  category?: { id: string; name: string; menuTypeId?: string | null };
  inventory?: any;
}

interface UseProductsOptions {
  categoryId?: string;
  search?: string;
  featured?: boolean;
  page?: number;
  limit?: number;
}

export function useProducts(options: UseProductsOptions = {}) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (options.categoryId) params.append('categoryId', options.categoryId);
      if (options.search) params.append('search', options.search);
      if (options.featured !== undefined) params.append('isFeatured', options.featured.toString());
      if (options.page !== undefined) params.append('page', options.page.toString());
      if (options.limit !== undefined) params.append('limit', options.limit.toString());

      const queryString = params.toString();
      // Use the dedicated category endpoint when categoryId is provided
      const url = options.categoryId
        ? `/products/category/${options.categoryId}`
        : queryString
        ? `/products?${queryString}`
        : '/products';

      // apiService.get returns ApiResponse<T> — actual data is in response.data
      const response = await apiService.get<any>(url);

      // Backend returns paginated: { data: [...], meta: {...} }
      // After transform interceptor: ApiResponse.data = { data: [...], meta: {...} }
      let arr: Product[] = [];
      if (Array.isArray(response.data)) {
        arr = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        arr = response.data.data;
      } else if (Array.isArray(response)) {
        arr = response as any;
      }

      setProducts(arr.filter((p) => p.isActive));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch products');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.categoryId, options.search, options.featured, options.page, options.limit]);

  return {
    products,
    loading,
    error,
    refresh: fetchProducts,
  };
}